// src/tenantAdmin/tenantAdminApi.js
//
// Every export names its workspace EXPLICITLY, as a required FIRST argument.
// The backend resolves the workspace `?tenant=` BEFORE `X-Tenant-Id`
// (dependencies.py) and `tenant` is an allowed strict query param on every
// /tenant-admin/* route, so the query param is the one that decides.
import { apiFetch } from '../auth/apiFetch.js';
import { buildApiError } from '../context/parseApiError.js';

const TENANT_ADMIN = '/api/v1/tenant-admin';

// ⛔ Every call names its workspace. Relying on the X-Tenant-Id header would act
// on the ACTIVE workspace — silently the wrong one once the page has a workspace
// selector. A missing tenant is a PROGRAMMING error, so it throws a TypeError
// before anything reaches the network: a loud stack trace at the call site
// instead of a write landing on someone else's workspace.
function requireTenant(tenant) {
  if (!tenant || typeof tenant !== 'string') {
    throw new TypeError('tenantAdminApi: a tenant id is required');
  }
  return tenant;
}

// `tenant` first, then the route's own params. Empty/undefined/null values are
// DROPPED rather than sent blank (`?email=` is not an empty filter).
function scoped(path, tenant, params = {}) {
  const qs = new URLSearchParams({ tenant: requireTenant(tenant) });
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  });
  return `${TENANT_ADMIN}${path}?${qs}`;
}

// The ONE place that talks to the network. `suppressTenantHeader: true` always:
// a stale X-Tenant-Id must never compete with the id the caller named.
// buildApiError, not a hand-rolled Error: it carries err.status AND err.code off
// the FLAT envelope, plus the top-level extras (`retry_after_seconds` of a 429,
// `previous_email`), which is what lets every caller branch on the code and word
// the refusal itself.
async function call(path, tenant, init = {}, params, fallback = 'Request failed') {
  const res = await apiFetch(scoped(path, tenant, params), { ...init, suppressTenantHeader: true });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw buildApiError(body, res.status, { fallbackMessage: `${fallback}: ${res.status}` });
  }
  if (res.status === 204) return null;
  return res.json().catch(() => null);
}

export async function listTenantUsers(tenant, { limit = 50, offset = 0, email } = {}) {
  return call('/users', tenant, {}, { limit, offset, email }, 'Failed to list users');
}

// Seats for the COMPANY that owns this workspace — read off the user-list
// ENVELOPE, never counted from the rows.
//
// GET /tenant-admin/users answers `user_seats_used` / `user_seat_limit` /
// `user_seat_source` next to `users` / `total`. A seat is a PERSON of the
// customer — distinct people across every workspace the company owns, plus its
// company-level (billing) admins — counted while they are active or still owe
// an unconsumed onboarding link. So the figure cannot be derived from one
// workspace's rows, which is exactly why it rides on the envelope.
//
// ⚠️ THREE answers, and they must stay distinguishable:
//   • all three null              → UNKNOWN (no customer, no plan, or the
//                                   lookup failed). Render no seat line at all.
//   • source 'unlimited'          → no ceiling; `limit` is null and that null is
//                                   NOT "unknown".
//   • source 'plan' | 'customer'  → a real ceiling. 'customer' is an
//                                   operator-set override for the company: a
//                                   bigger plan does not lift it.
//
// ⚠️ Deliberately a SEPARATE function from listTenantUsers, not a flag on it.
// The list page carries an email search box, and asking this route WITH
// `?email=` hands back the FILTERED page — reusing that call would let a
// keystroke in the search field skew the readout. This one never accepts an
// `email`, so the trap is unreachable rather than merely avoided.
//
// limit=1 because only the envelope counters are read (ge=1 on the route, so 0
// is not allowed).
export async function fetchTenantSeats(tenant) {
  const data = await call('/users', tenant, {}, { limit: 1, offset: 0 }, 'Failed to read seats');
  // Only the right type counts: anything else (absent, a string, a float) is
  // `null`, which the caller reads as "not told" and never as zero.
  return {
    used: Number.isInteger(data?.user_seats_used) ? data.user_seats_used : null,
    limit: Number.isInteger(data?.user_seat_limit) ? data.user_seat_limit : null,
    source: typeof data?.user_seat_source === 'string' ? data.user_seat_source : null,
  };
}

// CREATING A USER IS AN INVITATION.
//
// ⛔ The request body is `{email, role}` and NOTHING else: `CreateUserRequest` is
// `extra="forbid"`, so any extra key is a 422 — which is exactly what every
// submit from this dialog used to be, because it still sent a credential the
// route dropped on 2026-09-03.
//
// The account is created INACTIVE and receives a single-use link that lets the
// person choose their own credential. 201 answers `InviteUserResponse`
// ({user_id, email, tenant_id, role, assigned_at, email_sent, warning}):
// `email_sent: false` means the row exists but nobody was told — a platform
// administrator has to step in. ⛔ `warning` is fixed English prose; never
// render it (the caller words every outcome itself).
//
// Refusal order inside the route: role → email collision (409
// AUTH_EMAIL_EXISTS) → seat gate (409 USER_SEAT_LIMIT_REACHED). So an address
// that already exists gets the collision EVEN AT THE SEAT CAP, and the caller's
// attach fallback still runs.
export const createTenantUser = (tenant, { email, role = 'editor' }) =>
  call('/users', tenant, { method: 'POST', body: JSON.stringify({ email, role }) }, undefined, 'Failed to create user');

// ADDING AN ACCOUNT THAT ALREADY EXISTS — two outcomes, and the caller MUST
// read `status` to tell them apart:
//
//   201 {status:"attached", …, user_id}  — the person is in the workspace NOW.
//       Granted to a platform superadmin, or when the person already belongs
//       ONLY to this company and only to workspaces the caller administers.
//       No mail is sent; they see the workspace at their NEXT sign-in. Because
//       they already hold a company seat, this succeeds even at the seat cap.
//   202 {status:"invitation_sent", …, user_id:null, email_sent}  — everyone
//       else. Nothing changed yet: the person joins only by accepting the
//       emailed link, signed in as the invited account.
//
// Refusals (all decided before the branch above): 404 USER_NOT_FOUND; 409
// AUTH_USER_DEACTIVATED (switched off by an administrator) / AUTH_USER_PENDING_ONBOARDING
// (invited, password never set — BE 7c5dc05; before it both read as DEACTIVATED); 409
// CANNOT_ATTACH_SUPERADMIN; 409 USER_TENANT_ASSIGNMENT_EXISTS; 422 INVALID_ROLE;
// 409 USER_SEAT_LIMIT_REACHED (invitation path only); 429 RATE_LIMITED.
// ⛔ `warning` is fixed English prose — never render it.
export const attachTenantUser = (tenant, { email, role = 'editor' }) =>
  call('/users/attach', tenant, { method: 'POST', body: JSON.stringify({ email, role }) }, undefined, 'Failed to attach user');

// err.code survives buildApiError — the caller words 409
// USER_HAS_OTHER_MEMBERSHIPS / USER_HAS_OTHER_TENANTS itself, and the same for
// 409 CANNOT_DEACTIVATE_SELF / CANNOT_REMOVE_LAST_CUSTOMER_ADMIN.
export const deactivateTenantUser = (tenant, userId) =>
  call(`/users/${userId}/deactivate`, tenant, { method: 'POST' }, undefined, 'Failed to deactivate');

export const changeTenantUserRole = (tenant, userId, role) =>
  call(`/users/${userId}/role`, tenant, { method: 'PATCH', body: JSON.stringify({ role }) }, undefined, 'Failed to change role');

// 200 `{status, tenant_id, user_id, account_deleted, warning}`. ⭐ `account_deleted:
// true` (BE 7c5dc05) = the person never set a password and this was their last
// workspace or company, so the ACCOUNT and its link were deleted too — the caller
// words it as "invitation withdrawn". ⛔ `warning` is English prose — never render it.
export const removeTenantUser = (tenant, userId) =>
  call(`/users/${userId}`, tenant, { method: 'DELETE' }, undefined, 'Failed to remove user');

// Edit another tenant member's name/locale.
// PATCH semantics: pass ONLY the keys that changed. Omitted = unchanged, explicit
// null = clear. Dumping the whole form serializes untouched empty inputs as null
// and silently wipes stored names. buildApiError keeps err.status + err.code so
// callers can branch on CANNOT_EDIT_USER / USER_TENANT_ASSIGNMENT_NOT_FOUND /
// USER_NOT_FOUND, and on the 409 USER_HAS_OTHER_MEMBERSHIPS / USER_HAS_OTHER_TENANTS
// refusals this route can answer for a person who also belongs elsewhere.
export const updateTenantUserProfile = (tenant, userId, patch) =>
  call(`/users/${userId}/profile`, tenant, { method: 'PATCH', body: JSON.stringify(patch) }, undefined, 'Failed to update profile');

// Set another tenant member's credential directly. Mirrors the superadmin
// endpoint (POST /admin/users/{id}/password, same body). ⚠️ It answers 200 with a
// ResetPasswordResponse body, NOT 204 — the body carries nothing the UI needs, so
// it is deliberately left unread and this wrapper returns NOTHING. The route is
// LIVE on the backend (the earlier "not implemented yet" note was stale). It can
// also answer the 409 USER_HAS_OTHER_MEMBERSHIPS / USER_HAS_OTHER_TENANTS refusals.
export async function resetTenantUserPassword(tenant, userId, password) {
  await call(`/users/${userId}/password`, tenant, { method: 'POST', body: JSON.stringify({ password }) }, undefined, 'Failed to reset password');
}

// ── State-gated controls ────────────────────────────────────────────────────
// Each of the three below only makes sense in ONE row state; the list page
// decides WHEN it renders them (a resend needs `pending_invite`, an activate
// needs an onboarded-but-inactive row). ⛔ None of them applies to a
// `via:"customer"` row: every per-user route 404s on those.

// Sends a FRESH single-use link and KILLS the previous one. → {user_id, email,
// email_sent}; `email_sent: false` is not success. Refusals: 409
// USER_NOT_PENDING_INVITE / USER_HAS_OTHER_*, 403 CANNOT_EDIT_USER, 404, 429
// RATE_LIMITED (shares the invite/attach caps: 20/h per admin, 3 per 10 min per
// address, `retry_after_seconds`).
export const resendTenantInvite = (tenant, userId) =>
  call(`/users/${userId}/resend-invite`, tenant, { method: 'POST' }, undefined, 'Failed to resend invite');

// Re-enables an account that ALREADY chose a credential — effective at once, no
// new sign-in and no mail. → {user_id, email, is_active, status:"activated" |
// "already_active"}. Refusals: 409 USER_NEVER_ONBOARDED (⚠️ that person needs a
// resend, not an activate), 409 USER_SEAT_LIMIT_REACHED (it takes a seat back),
// 409 USER_HAS_OTHER_*, 403, 404.
export const activateTenantUser = (tenant, userId) =>
  call(`/users/${userId}/activate`, tenant, { method: 'POST' }, undefined, 'Failed to activate');

// Fix a mistyped address. → {user_id, email, previous_email, warning} — show the
// pair, ⛔ never the `warning` (English prose). Nobody is told out of band, and a
// still-invited person's old link is dead, so the caller offers a resend.
// Refusals: 409 CANNOT_EDIT_OWN_EMAIL / AUTH_EMAIL_EXISTS / USER_HAS_OTHER_*,
// 403 CANNOT_EDIT_USER, 404.
export const correctTenantUserEmail = (tenant, userId, email) =>
  call(`/users/${userId}/email`, tenant, { method: 'PATCH', body: JSON.stringify({ email }) }, undefined, 'Failed to correct email');

// Rename the workspace itself (1–100 chars) → {id, display_name, is_active,
// created_at}, no `warning`. It also re-syncs the workspace's brand-identity
// company name on the backend. 422 INVALID_DISPLAY_NAME. ⚠️ The new name reaches
// the rest of the app only from the next sign-in: `tenants[]` is minted at
// login.
export const renameTenant = (tenant, displayName) =>
  call('/tenant', tenant, { method: 'PATCH', body: JSON.stringify({ display_name: displayName }) }, undefined, 'Failed to rename workspace');
